import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    Avatar
} from '@mui/material';
import { ArrowBackIcon } from 'icons/materialIcons';
import { updateAccessory } from 'apis/accessoryApi';
import { getCardsByUser } from 'apis/userApi';
import { useAuth } from 'contexts/AuthContext';
import avatarPlaceholder from 'images/avatar-cropped.png';

const TeamAccessoryEdit = ({ accessory, onCancel, onSave }) => {
    const [accessoryName, setAccessoryName] = useState(accessory?.name || '');
    const [selectedCardId, setSelectedCardId] = useState(
        accessory?.content?.data?.cardId || ''
    );
    const [userCards, setUserCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const { authState } = useAuth();

    useEffect(() => {
        const fetchCards = async () => {
            if (!authState?.user?.userId) return;

            setLoading(true);
            try {
                const cards = await getCardsByUser(authState.user.userId);
                setUserCards(cards);
            } catch (error) {
                console.error('Failed to fetch cards:', error);
                setError('Failed to load cards. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchCards();
    }, [authState?.user?.userId]);

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const updateData = {
                name: accessoryName,
                content: {
                    type: 'business_card',
                    data: {
                        cardId: selectedCardId,
                        type: 'card_link'
                    }
                }
            };

            const result = await updateAccessory(accessory.serialNumber, updateData);

            // On success, directly call onSave and navigate
            if (onSave) {
                onSave(result);
            }
            onCancel(); // This will handle the navigation

        } catch (error) {
            console.error('Failed to update accessory:', error);
            setError(error.response?.data?.message || 'Failed to update accessory');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onCancel}
                    sx={{
                        color: '#4f4f4f',
                        textTransform: 'none',
                        fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}
                >
                    Back
                </Button>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        color: '#4f4f4f'
                    }}
                >
                    Edit Accessory
                </Typography>
            </Box>

            <Box sx={{ maxWidth: { xs: '100%', sm: '600px' } }}>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 1,
                        color: '#4f4f4f',
                        fontWeight: 500
                    }}
                >
                    Accessory Name
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    value={accessoryName}
                    onChange={(e) => {
                        setAccessoryName(e.target.value);
                        setError('');
                    }}
                    sx={{ mb: 3 }}
                />

                <Typography
                    variant="body2"
                    sx={{
                        mb: 1,
                        color: '#4f4f4f',
                        fontWeight: 500
                    }}
                >
                    Connected Card
                </Typography>
                <FormControl
                    fullWidth
                    size="small"
                    sx={{ mb: 3 }}
                >
                    <Select
                        value={selectedCardId}
                        onChange={(e) => {
                            setSelectedCardId(e.target.value);
                            setError('');
                        }}
                        disabled={loading || saving}
                        error={!!error && !selectedCardId}
                        sx={{
                            borderRadius: '12px',
                            '& .MuiSelect-select': {
                                borderRadius: '12px',
                                padding: '10px 14px',
                            },
                            '& fieldset': {
                                border: 'none',
                                borderRadius: '12px',
                            }
                        }}
                    >
                        {userCards.map((card) => (
                            <MenuItem key={card._id} value={card._id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Avatar
                                        src={card.profilePictureUrl || avatarPlaceholder}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            mr: 2
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '0.875rem' }}>
                                        {card.contact_info?.fullName || 'Unnamed Card'}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box sx={{
                    p: 2,
                    bgcolor: '#f8f9fa',
                    borderRadius: 1,
                    mb: 3
                }}>
                    <Typography sx={{
                        fontFamily: 'monospace',
                        color: '#666',
                        fontSize: '0.8rem',
                        mb: 1
                    }}>
                        Serial Number: {accessory.serialNumber}
                    </Typography>
                    <Typography sx={{
                        color: '#666',
                        fontSize: '0.8rem'
                    }}>
                        Status: {accessory.status}
                    </Typography>
                </Box>

                {error && (
                    <Typography
                        sx={{
                            color: 'error.main',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            marginBottom: 2,
                            textAlign: 'left'
                        }}
                    >
                        {error}
                    </Typography>
                )}

                <Box sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 2 },
                    justifyContent: 'flex-end',
                    flexDirection: { xs: 'column', sm: 'row' }
                }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={saving}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            color: '#666',
                            borderColor: '#ddd',
                            padding: { xs: '10px 16px', sm: '8px 24px' },
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            '&:hover': {
                                borderColor: '#999',
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !selectedCardId}
                        sx={{
                            backgroundColor: '#000',
                            color: '#fff',
                            textTransform: 'none',
                            borderRadius: '8px',
                            padding: { xs: '10px 16px', sm: '8px 24px' },
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            '&:hover': {
                                backgroundColor: '#333'
                            }
                        }}
                    >
                        {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default TeamAccessoryEdit;